import React, { useState, useEffect } from 'react';
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle,
  CardDescription 
} from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { ScrollArea } from './ui/scroll-area';
import { 
  User,
  Globe,
  Monitor,
  Clock,
  MessageSquare,
  Target,
  ShoppingCart,
  TrendingUp,
  UserCheck,
  Sparkles,
  FileText,
  Activity,
  Loader2,
  X,
  ExternalLink,
  Video,
  Brain,
  ChevronRight
} from 'lucide-react';
import { API_URL } from '../config/api';
import { formatDistanceToNow, format } from 'date-fns';
import { EnhancedChatMessage } from './chat/EnhancedChatMessage';

interface SessionDetailViewProps {
  sessionId: string;
  onClose?: () => void;
}

interface EnrichedSession {
  session: {
    id: string;
    siteId: string;
    siteName: string;
    siteUrl: string;
    start: string;
    lastActivity: string;
    duration: number;
    isActive: boolean;
    hasOpenReplay: boolean;
    openReplaySessionId?: string;
  };
  visitorProfile?: {
    maturityLevel: string;
    maturityScore: number;
    engagementScore: number;
    totalSessions: number;
    totalPageViews: number;
    totalDuration: number;
    microConversions: string[];
    firstVisit?: string;
    lastVisit?: string;
  };
  customerProfile?: {
    email: string;
    totalSessions: number;
    preferredProducts: string[];
    segment?: string;
    lifetimeValue: number;
    firstVisit?: string;
    lastVisit?: string;
    purchaseHistory: any[];
  };
  navigation: {
    pagesVisited: any[];
    currentPage: any;
    currentPageSemantics?: any;
  };
  interactions: {
    totalCount: number;
    firstInteraction?: string;
    lastInteraction?: string;
    messages: any[];
  };
  agentInsights: Record<string, any[]>;
}

export default function SessionDetailView({ sessionId, onClose }: SessionDetailViewProps) {
  const [loading, setLoading] = useState(true);
  const [sessionData, setSessionData] = useState<EnrichedSession | null>(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [agentInsights, setAgentInsights] = useState<any[]>([]);
  const [loadingInsights, setLoadingInsights] = useState(false);

  useEffect(() => {
    fetchSessionDetails();
  }, [sessionId]);

  const fetchSessionDetails = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/active-sessions/${sessionId}/details`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch session details');
      }
      
      const data = await response.json();
      setSessionData(data);
    } catch (error) {
      console.error('Error fetching session details:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAgentInsights = async () => {
    try {
      setLoadingInsights(true);
      const response = await fetch(`${API_URL}/active-sessions/${sessionId}/agent-stream`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch agent insights');
      }
      
      const data = await response.json();
      setAgentInsights(data.insights);
    } catch (error) {
      console.error('Error fetching agent insights:', error);
    } finally {
      setLoadingInsights(false);
    }
  };

  const formatDuration = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  };

  const getMaturityColor = (level: string) => {
    switch (level) {
      case 'explorer': return 'bg-blue-100 text-blue-800';
      case 'interested': return 'bg-yellow-100 text-yellow-800';
      case 'engaged': return 'bg-orange-100 text-orange-800';
      case 'qualified': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getMaturityIcon = (level: string) => {
    switch (level) {
      case 'explorer': return '🔍';
      case 'interested': return '💡';
      case 'engaged': return '🎯';
      case 'qualified': return '✅';
      default: return '👤';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!sessionData) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Session not found</p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="p-4 border-b">
        <h2 className="text-xl font-semibold">Session Details</h2>
        <p className="text-sm text-muted-foreground">
          {sessionData.session.siteName} • Session {sessionData.session.id.substring(0, 12)}...
        </p>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="h-full flex flex-col">
          <TabsList className="px-4">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="customer">Customer</TabsTrigger>
            <TabsTrigger value="intent" onClick={() => activeTab === 'intent' && fetchAgentInsights()}>
              Customer Intent
            </TabsTrigger>
            <TabsTrigger value="navigation">Navigation</TabsTrigger>
            <TabsTrigger value="interactions">Interactions</TabsTrigger>
            <TabsTrigger value="insights" onClick={() => activeTab === 'insights' && fetchAgentInsights()}>
              AI Insights
            </TabsTrigger>
          </TabsList>

          <ScrollArea className="flex-1">
            <div className="p-4">
              {/* Overview Tab */}
              <TabsContent value="overview" className="mt-0">
                <div className="space-y-4">
                  {/* Session Info */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Session Information</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-sm text-muted-foreground">Status</span>
                        <Badge variant={sessionData.session.isActive ? "default" : "secondary"}>
                          {sessionData.session.isActive ? "Active" : "Idle"}
                        </Badge>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-muted-foreground">Duration</span>
                        <span className="text-sm font-medium">{formatDuration(sessionData.session.duration)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-muted-foreground">Started</span>
                        <span className="text-sm">{formatDistanceToNow(new Date(sessionData.session.start), { addSuffix: true })}</span>
                      </div>
                      {sessionData.session.hasOpenReplay && (
                        <div className="flex justify-between">
                          <span className="text-sm text-muted-foreground">Recording</span>
                          <Badge variant="outline" className="text-xs">
                            <Video className="h-3 w-3 mr-1" />
                            Available
                          </Badge>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Visitor Maturity */}
                  {sessionData.visitorProfile && (
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg">Visitor Maturity</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <span className="text-2xl">{getMaturityIcon(sessionData.visitorProfile.maturityLevel)}</span>
                            <div>
                              <p className="font-medium capitalize">{sessionData.visitorProfile.maturityLevel}</p>
                              <p className="text-sm text-muted-foreground">Maturity Level</p>
                            </div>
                          </div>
                          <Badge className={getMaturityColor(sessionData.visitorProfile.maturityLevel)}>
                            Score: {sessionData.visitorProfile.maturityScore}/100
                          </Badge>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <p className="text-sm text-muted-foreground">Engagement</p>
                            <p className="text-lg font-semibold">{sessionData.visitorProfile.engagementScore}%</p>
                          </div>
                          <div>
                            <p className="text-sm text-muted-foreground">Total Sessions</p>
                            <p className="text-lg font-semibold">{sessionData.visitorProfile.totalSessions}</p>
                          </div>
                        </div>

                        {sessionData.visitorProfile.microConversions.length > 0 && (
                          <div>
                            <p className="text-sm text-muted-foreground mb-2">Micro Conversions</p>
                            <div className="flex flex-wrap gap-2">
                              {sessionData.visitorProfile.microConversions.map((conversion, idx) => (
                                <Badge key={idx} variant="secondary" className="text-xs">
                                  {conversion}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  )}

                  {/* Quick Stats */}
                  <div className="grid grid-cols-3 gap-4">
                    <Card>
                      <CardContent className="pt-6">
                        <div className="text-center">
                          <Globe className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                          <p className="text-2xl font-bold">{sessionData.navigation.pagesVisited.length}</p>
                          <p className="text-xs text-muted-foreground">Pages Visited</p>
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-6">
                        <div className="text-center">
                          <MessageSquare className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                          <p className="text-2xl font-bold">{sessionData.interactions.totalCount}</p>
                          <p className="text-xs text-muted-foreground">Interactions</p>
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-6">
                        <div className="text-center">
                          <Brain className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                          <p className="text-2xl font-bold">{Object.keys(sessionData.agentInsights).length}</p>
                          <p className="text-xs text-muted-foreground">AI Agents Active</p>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              </TabsContent>

              {/* Customer Tab */}
              <TabsContent value="customer" className="mt-0">
                <div className="space-y-4">
                  {sessionData.customerProfile ? (
                    <>
                      <Card>
                        <CardHeader>
                          <CardTitle className="text-lg">Customer Profile</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-3">
                          <div className="flex items-center gap-3">
                            <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                              <User className="h-6 w-6 text-primary" />
                            </div>
                            <div className="flex-1">
                              <p className="font-medium">{sessionData.customerProfile.email}</p>
                              {sessionData.customerProfile.segment && (
                                <Badge variant="outline" className="text-xs mt-1">
                                  {sessionData.customerProfile.segment}
                                </Badge>
                              )}
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-4 pt-4">
                            <div>
                              <p className="text-sm text-muted-foreground">Lifetime Value</p>
                              <p className="text-lg font-semibold">
                                ${sessionData.customerProfile.lifetimeValue.toLocaleString()}
                              </p>
                            </div>
                            <div>
                              <p className="text-sm text-muted-foreground">Total Sessions</p>
                              <p className="text-lg font-semibold">{sessionData.customerProfile.totalSessions}</p>
                            </div>
                          </div>

                          {sessionData.customerProfile.firstVisit && (
                            <div className="pt-2">
                              <p className="text-sm text-muted-foreground">Customer Since</p>
                              <p className="text-sm">{format(new Date(sessionData.customerProfile.firstVisit), 'PPP')}</p>
                            </div>
                          )}
                        </CardContent>
                      </Card>

                      {sessionData.customerProfile.preferredProducts.length > 0 && (
                        <Card>
                          <CardHeader>
                            <CardTitle className="text-lg">Preferred Products</CardTitle>
                          </CardHeader>
                          <CardContent>
                            <div className="space-y-2">
                              {sessionData.customerProfile.preferredProducts.map((product, idx) => (
                                <div key={idx} className="flex items-center gap-2">
                                  <ShoppingCart className="h-4 w-4 text-muted-foreground" />
                                  <span className="text-sm">{product}</span>
                                </div>
                              ))}
                            </div>
                          </CardContent>
                        </Card>
                      )}

                      {sessionData.customerProfile.purchaseHistory.length > 0 && (
                        <Card>
                          <CardHeader>
                            <CardTitle className="text-lg">Purchase History</CardTitle>
                          </CardHeader>
                          <CardContent>
                            <div className="space-y-3">
                              {sessionData.customerProfile.purchaseHistory.slice(0, 5).map((purchase, idx) => (
                                <div key={idx} className="flex justify-between items-center">
                                  <div>
                                    <p className="text-sm font-medium">{purchase.product}</p>
                                    <p className="text-xs text-muted-foreground">
                                      {format(new Date(purchase.date), 'PP')}
                                    </p>
                                  </div>
                                  <p className="text-sm font-semibold">${purchase.amount}</p>
                                </div>
                              ))}
                            </div>
                          </CardContent>
                        </Card>
                      )}
                    </>
                  ) : (
                    <Card>
                      <CardContent className="py-12 text-center">
                        <UserCheck className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                        <p className="text-muted-foreground">Anonymous Visitor</p>
                        <p className="text-sm text-muted-foreground mt-1">
                          Customer profile will appear once the visitor is identified
                        </p>
                      </CardContent>
                    </Card>
                  )}
                </div>
              </TabsContent>

              {/* Customer Intent Tab */}
              <TabsContent value="intent" className="mt-0">
                <div className="space-y-4">
                  {(() => {
                    // Find the latest customer intent from agent insights
                    const customerIntentInsights = agentInsights.filter(
                      insight => insight.agentType === 'customer-intent' && insight.status === 'completed'
                    );
                    const latestIntent = customerIntentInsights[0];
                    
                    if (!latestIntent || !latestIntent.response) {
                      return (
                        <Card>
                          <CardContent className="py-12 text-center">
                            <Target className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                            <p className="text-muted-foreground">No customer intent analysis available yet</p>
                            <Button 
                              variant="outline" 
                              size="sm" 
                              className="mt-4"
                              onClick={fetchAgentInsights}
                            >
                              Refresh Analysis
                            </Button>
                          </CardContent>
                        </Card>
                      );
                    }
                    
                    const intentData = latestIntent.response;
                    
                    return (
                      <>
                        {/* Primary Intent */}
                        <Card>
                          <CardHeader>
                            <CardTitle className="text-lg">Current Intent</CardTitle>
                            <CardDescription>
                              Last analyzed {formatDistanceToNow(new Date(latestIntent.timestamp), { addSuffix: true })}
                            </CardDescription>
                          </CardHeader>
                          <CardContent className="space-y-4">
                            {intentData.intent && (
                              <div className="space-y-3">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-3">
                                    <Target className="h-5 w-5 text-primary" />
                                    <div>
                                      <p className="font-medium capitalize">{intentData.intent.primaryIntent || 'Unknown'}</p>
                                      <p className="text-sm text-muted-foreground">Primary Intent</p>
                                    </div>
                                  </div>
                                  <Badge className="bg-primary/10 text-primary">
                                    {Math.round((intentData.intent.confidence || 0) * 100)}% Confidence
                                  </Badge>
                                </div>
                                
                                {intentData.intent.intent_topic && (
                                  <div>
                                    <p className="text-sm text-muted-foreground">Topic</p>
                                    <Badge variant="outline">{intentData.intent.intent_topic}</Badge>
                                  </div>
                                )}
                              </div>
                            )}
                            
                            {/* Sentiment & Urgency */}
                            <div className="grid grid-cols-2 gap-4 pt-4">
                              {intentData.sentiment && (
                                <div>
                                  <p className="text-sm text-muted-foreground">Sentiment</p>
                                  <p className="font-medium capitalize">{intentData.sentiment.overall}</p>
                                  <p className="text-xs text-muted-foreground">Score: {intentData.sentiment.score}</p>
                                </div>
                              )}
                              {intentData.urgency && (
                                <div>
                                  <p className="text-sm text-muted-foreground">Urgency</p>
                                  <p className="font-medium capitalize">{intentData.urgency.level}</p>
                                  <p className="text-xs text-muted-foreground">Score: {intentData.urgency.score}</p>
                                </div>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                        
                        {/* What AI Sees */}
                        {intentData.whatISee && (
                          <Card>
                            <CardHeader>
                              <CardTitle className="text-lg">Visual Analysis</CardTitle>
                              <CardDescription>What the AI observed on the page</CardDescription>
                            </CardHeader>
                            <CardContent>
                              <p className="text-sm">{intentData.whatISee}</p>
                            </CardContent>
                          </Card>
                        )}
                        
                        {/* Analysis Notes */}
                        {intentData.analysisNotes && (
                          <Card>
                            <CardHeader>
                              <CardTitle className="text-lg">Analysis Notes</CardTitle>
                            </CardHeader>
                            <CardContent>
                              <p className="text-sm">{intentData.analysisNotes}</p>
                            </CardContent>
                          </Card>
                        )}
                        
                        {/* Suggested Questions */}
                        {intentData.suggestedQuestions && intentData.suggestedQuestions.length > 0 && (
                          <Card>
                            <CardHeader>
                              <CardTitle className="text-lg">Suggested Questions</CardTitle>
                              <CardDescription>Questions the customer might ask next</CardDescription>
                            </CardHeader>
                            <CardContent>
                              <div className="space-y-2">
                                {intentData.suggestedQuestions.map((question: string, idx: number) => (
                                  <div key={idx} className="flex items-start gap-2">
                                    <MessageSquare className="h-4 w-4 text-muted-foreground mt-0.5" />
                                    <p className="text-sm">{question}</p>
                                  </div>
                                ))}
                              </div>
                            </CardContent>
                          </Card>
                        )}
                        
                        {/* Next Intents */}
                        {intentData.nextIntents && intentData.nextIntents.length > 0 && (
                          <Card>
                            <CardHeader>
                              <CardTitle className="text-lg">Predicted Next Actions</CardTitle>
                              <CardDescription>Likely customer actions based on current intent</CardDescription>
                            </CardHeader>
                            <CardContent>
                              <div className="space-y-2">
                                {intentData.nextIntents.map((next: any, idx: number) => (
                                  <div key={idx} className="flex items-center justify-between">
                                    <span className="text-sm capitalize">{next.intent.replace(/_/g, ' ')}</span>
                                    <Badge variant="secondary" className="text-xs">
                                      {Math.round(next.confidence * 100)}%
                                    </Badge>
                                  </div>
                                ))}
                              </div>
                            </CardContent>
                          </Card>
                        )}
                      </>
                    );
                  })()}
                </div>
              </TabsContent>

              {/* Navigation Tab */}
              <TabsContent value="navigation" className="mt-0">
                <div className="space-y-4">
                  {sessionData.navigation.currentPage && (
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg">Current Page</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3">
                          <div>
                            <p className="font-medium">{sessionData.navigation.currentPage.title || 'Untitled Page'}</p>
                            <p className="text-sm text-muted-foreground truncate">{sessionData.navigation.currentPage.url}</p>
                          </div>
                          
                          {sessionData.navigation.currentPageSemantics && (
                            <div className="pt-3 space-y-2">
                              {sessionData.navigation.currentPageSemantics.topics && (
                                <div>
                                  <p className="text-xs text-muted-foreground mb-1">Topics</p>
                                  <div className="flex flex-wrap gap-1">
                                    {sessionData.navigation.currentPageSemantics.topics.map((topic: string, idx: number) => (
                                      <Badge key={idx} variant="secondary" className="text-xs">
                                        {topic}
                                      </Badge>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Page Journey</CardTitle>
                      <CardDescription>
                        {sessionData.navigation.pagesVisited.length} pages visited in this session
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {sessionData.navigation.pagesVisited.map((page, idx) => (
                          <div key={idx} className="flex items-start gap-3">
                            <div className="flex flex-col items-center">
                              <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-xs font-medium">
                                {idx + 1}
                              </div>
                              {idx < sessionData.navigation.pagesVisited.length - 1 && (
                                <div className="w-0.5 h-8 bg-border mt-1" />
                              )}
                            </div>
                            <div className="flex-1">
                              <p className="text-sm font-medium">{page.title || 'Untitled Page'}</p>
                              <p className="text-xs text-muted-foreground truncate">{page.url}</p>
                              <div className="flex items-center gap-4 mt-1">
                                <p className="text-xs text-muted-foreground">
                                  {formatDistanceToNow(new Date(page.visited_at), { addSuffix: true })}
                                </p>
                                {page.interactions_on_page > 0 && (
                                  <Badge variant="secondary" className="text-xs">
                                    {page.interactions_on_page} interactions
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              {/* Interactions Tab */}
              <TabsContent value="interactions" className="mt-0">
                <div className="space-y-4">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Conversation History</CardTitle>
                      <CardDescription>
                        {sessionData.interactions.totalCount} messages exchanged
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      {sessionData.interactions.messages.length > 0 ? (
                        <div className="space-y-6">
                          {sessionData.interactions.messages.map((msg, idx) => (
                            <EnhancedChatMessage
                              key={idx}
                              message={{
                                ...msg,
                                timestamp: msg.timestamp || new Date().toISOString()
                              }}
                            />
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-8">
                          <MessageSquare className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                          <p className="text-muted-foreground">No interactions yet</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              {/* AI Insights Tab */}
              <TabsContent value="insights" className="mt-0">
                <div className="space-y-4">
                  {loadingInsights ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="h-8 w-8 animate-spin" />
                    </div>
                  ) : agentInsights.length > 0 ? (
                    agentInsights.map((insight, idx) => (
                      <Card key={idx}>
                        <CardHeader>
                          <div className="flex items-center justify-between">
                            <CardTitle className="text-lg">{insight.agentType}</CardTitle>
                            <Badge variant="outline" className="text-xs">
                              {formatDistanceToNow(new Date(insight.timestamp), { addSuffix: true })}
                            </Badge>
                          </div>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-3">
                            {insight.response && (
                              <div>
                                <p className="text-sm font-medium mb-1">Analysis</p>
                                <pre className="text-xs bg-muted p-3 rounded-md overflow-x-auto">
                                  {JSON.stringify(insight.response, null, 2)}
                                </pre>
                              </div>
                            )}
                            <div className="flex items-center gap-4 text-xs text-muted-foreground">
                              {insight.confidence && (
                                <span>Confidence: {Math.round(insight.confidence * 100)}%</span>
                              )}
                              {insight.processingTime && (
                                <span>Processing: {insight.processingTime}ms</span>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  ) : (
                    <Card>
                      <CardContent className="py-12 text-center">
                        <Brain className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                        <p className="text-muted-foreground">No AI insights available yet</p>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="mt-4"
                          onClick={fetchAgentInsights}
                        >
                          Refresh Insights
                        </Button>
                      </CardContent>
                    </Card>
                  )}
                </div>
              </TabsContent>
            </div>
          </ScrollArea>
        </Tabs>
      </div>
    </div>
  );
}